const BLANK_META_KEY = '__blankFilterType'

const BLANK_FILTER_TYPES = {
  BLANK: 'blank',
  NOT_BLANK: 'notBlank',
}

const isBlankValue = value => value === null || value === ''

const removeMetaFromFilter = filter => {
  if (!filter) return filter
  const sanitized = { ...filter }
  delete sanitized[BLANK_META_KEY]

  if (Array.isArray(sanitized.conditions)) {
    sanitized.conditions = sanitized.conditions.map(condition =>
      removeMetaFromFilter(condition)
    )
  }

  if (sanitized.condition1) {
    sanitized.condition1 = removeMetaFromFilter(sanitized.condition1)
  }

  if (sanitized.condition2) {
    sanitized.condition2 = removeMetaFromFilter(sanitized.condition2)
  }

  return sanitized
}

const buildStringBlankFilter = (
  mode = BLANK_FILTER_TYPES.BLANK,
  filterType = 'text'
) => {
  const isBlank = mode === BLANK_FILTER_TYPES.BLANK
  const comparator = isBlank ? 'equals' : 'notEqual'
  const logicalOperator = isBlank ? 'OR' : 'AND'

  return {
    filterType,
    operator: logicalOperator,
    condition1: {
      filterType,
      type: comparator,
      filter: '',
    },
    condition2: {
      filterType,
      type: comparator,
      filter: null,
    },
    [BLANK_META_KEY]: mode,
  }
}

const buildNumberBlankFilter = (mode = BLANK_FILTER_TYPES.BLANK) => {
  const comparator = mode === BLANK_FILTER_TYPES.BLANK ? 'equals' : 'notEqual'
  return {
    filterType: 'number',
    type: comparator,
    filter: null,
    [BLANK_META_KEY]: mode,
  }
}

const normalizeTextFilter = filter => {
  if (!filter) return null
  if (!filter.type) return { ...filter }

  if (filter.type === 'blank') {
    return buildStringBlankFilter(BLANK_FILTER_TYPES.BLANK, filter.filterType)
  }

  if (filter.type === 'notBlank') {
    return buildStringBlankFilter(BLANK_FILTER_TYPES.NOT_BLANK, filter.filterType)
  }

  return { ...filter }
}

const normalizeNumberFilter = filter => {
  if (!filter) return null
  if (!filter.type) return { ...filter }

  if (filter.type === 'blank') {
    return buildNumberBlankFilter(BLANK_FILTER_TYPES.BLANK)
  }

  if (filter.type === 'notBlank') {
    return buildNumberBlankFilter(BLANK_FILTER_TYPES.NOT_BLANK)
  }

  return { ...filter }
}

const normalizeSetFilter = filter => {
  if (!filter || !Array.isArray(filter.values)) {
    return filter ? { ...filter } : null
  }

  const hasOnlyBlankSelections =
    filter.values.length > 0 && filter.values.every(isBlankValue)

  if (hasOnlyBlankSelections) {
    return buildStringBlankFilter(BLANK_FILTER_TYPES.BLANK)
  }

  const sanitizedValues = filter.values.filter(value => !isBlankValue(value))

  return {
    ...filter,
    values: sanitizedValues,
  }
}

const normalizeFilterEntry = filter => {
  if (!filter) return null

  switch (filter.filterType) {
    case 'text':
      return normalizeTextFilter(filter)
    case 'number':
      return normalizeNumberFilter(filter)
    case 'set':
      return normalizeSetFilter(filter)
    default:
      return { ...filter }
  }
}

const normalizeFilterModel = filterModel => {
  if (!filterModel || typeof filterModel !== 'object') {
    return null
  }

  const normalized = Object.entries(filterModel).reduce(
    (accumulator, [field, filter]) => {
      const normalizedFilter = normalizeFilterEntry(filter)
      if (normalizedFilter) {
        accumulator[field] = normalizedFilter
      }
      return accumulator
    },
    {}
  )

  return Object.keys(normalized).length ? normalized : null
}

const stripFilterMeta = filterModel => {
  if (!filterModel) return filterModel
  const sanitized = Object.entries(filterModel).reduce(
    (accumulator, [field, filter]) => {
      if (!filter) return accumulator
      accumulator[field] = removeMetaFromFilter(filter)
      return accumulator
    },
    {}
  )

  return Object.keys(sanitized).length ? sanitized : null
}

// MAM geometry field patterns — server stores SI units (m / m²), display/filter in imperial (ft / ft²)
const MAM_AREA_PATTERN = /^mam[_\s](area|acreage)/
const MAM_PERIMETER_PATTERN = /^mam[_\s](perimeter|perim|length|len)/

const getMamConversionFactor = fieldKey => {
  const key = fieldKey.toLowerCase()
  if (MAM_AREA_PATTERN.test(key)) return 10.7639 // m² → ft²
  if (MAM_PERIMETER_PATTERN.test(key)) return 3.28084 // m → ft
  return null
}

const convertNumberFilterToSi = (filter, imperialFactor) => {
  if (!filter) return filter
  const result = { ...filter }
  if (result.filter != null) result.filter = result.filter / imperialFactor
  if (result.filterTo != null) result.filterTo = result.filterTo / imperialFactor
  if (Array.isArray(result.conditions)) {
    result.conditions = result.conditions.map(condition =>
      convertNumberFilterToSi(condition, imperialFactor)
    )
  }
  if (result.condition1)
    result.condition1 = convertNumberFilterToSi(result.condition1, imperialFactor)
  if (result.condition2)
    result.condition2 = convertNumberFilterToSi(result.condition2, imperialFactor)
  return result
}

// Converts a complete AG Grid filterModel: any MAM geometry field values
// entered in imperial (ft / ft²) are divided back to SI (m / m²) so the
// server and MapLibre both compare against their stored raw data correctly.
const convertFilterModelMamToSi = filterModel => {
  if (!filterModel || typeof filterModel !== 'object') return filterModel
  return Object.entries(filterModel).reduce((acc, [field, filter]) => {
    const factor = getMamConversionFactor(field)
    acc[field] = factor ? convertNumberFilterToSi(filter, factor) : filter
    return acc
  }, {})
}

export {
  BLANK_META_KEY,
  BLANK_FILTER_TYPES,
  normalizeFilterModel,
  stripFilterMeta,
  isBlankValue,
  buildStringBlankFilter,
  buildNumberBlankFilter,
  MAM_AREA_PATTERN,
  MAM_PERIMETER_PATTERN,
  getMamConversionFactor,
  convertFilterModelMamToSi,
}
